import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import PhonelinkSetupOutlined from '@mui/icons-material/PhonelinkSetupOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import { useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { serializeBooleanRecordToParam } from 'v2/filters/filterUrlParams';
import { filterAccessDefault } from 'v2/filters/users/userFilterConstants';
import { RowsProps } from 'v2/pages/Dashboard/cards/Card';
import { getSeverityColors } from 'v2/pages/Dashboard/hooks/getSeverityColors';
import { USERS_OVERVIEW_PATH } from 'v2/paths';
import { formatCountLabel } from 'v2/utils/formatCountLabel';

interface Props {
  usersConfigDisconnected: number;
  usersDisconnectedWithoutCard: number;
}

type UserStatus = 'disconnectedWithoutCard' | 'configDisconnected';

const USER_ORDER: Record<UserStatus, number> = {
  disconnectedWithoutCard: 0,
  configDisconnected: 1
};

const useUsersRows = ({ usersConfigDisconnected, usersDisconnectedWithoutCard }: Props): RowsProps[] => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return useMemo(() => {
    const severityColors = getSeverityColors(theme);

    type UserRow = Pick<RowsProps, 'Icon' | 'text'> & {
      count: number;
      severity: 'error' | 'warning';
      status: UserStatus;
      onClick?: () => void;
    };

    const rows = (
      [
        {
          count: usersDisconnectedWithoutCard,
          text: formatCountLabel(
            usersDisconnectedWithoutCard,
            translation.dashboard_users_access_disconnected_without_card_singular,
            translation.dashboard_users_access_disconnected_without_card_plural
          ),
          Icon: PhoneIphoneOutlined,
          severity: 'error',
          status: 'disconnectedWithoutCard',
          onClick: () => {
            const access = serializeBooleanRecordToParam({
              ...filterAccessDefault,
              accessAppDisconnected: true,
              noCard: true
            });
            navigate(`${USERS_OVERVIEW_PATH}?access=${access}`);
          }
        },
        {
          count: usersConfigDisconnected,
          text: formatCountLabel(
            usersConfigDisconnected,
            translation.dashboard_users_config_disconnected_singular,
            translation.dashboard_users_config_disconnected_plural
          ),
          Icon: PhonelinkSetupOutlined,
          severity: 'error',
          status: 'configDisconnected',
          onClick: () => {
            const access = serializeBooleanRecordToParam({
              ...filterAccessDefault,
              configAppDisconnected: true
            });
            navigate(`${USERS_OVERVIEW_PATH}?access=${access}`);
          }
        }
      ] satisfies UserRow[]
    ).filter((row) => row.count > 0);

    const sortedRows = [...rows].sort((a, b) => USER_ORDER[a.status] - USER_ORDER[b.status]);

    if (sortedRows.length === 0) {
      return [
        {
          Icon: ThumbUpOutlined,
          text: translation.all_users_are_up_to_date,
          onClick: () => navigate(USERS_OVERVIEW_PATH),
          ...severityColors.success
        }
      ];
    }

    return sortedRows.map((row) => ({
      Icon: row.Icon,
      text: row.text,
      onClick: row.onClick,
      ...severityColors[row.severity]
    }));
  }, [usersConfigDisconnected, usersDisconnectedWithoutCard, translation, theme, navigate]);
};

export default useUsersRows;
